/**
 * ReferralCard — Two-sided referral rewards surface.
 *
 * "Invite a colleague — you both earn 1 report credit when they unlock their first paid report."
 *
 * Shows the current user's referral code, a copy-friendly share link, and their
 * rewarded/cap progress. Backed by GET /api/referrals/me.
 *
 * Auth-gated: parent must already have `user` from AuthContext.
 */
import { useEffect, useState } from "react";
import { Copy, Gift, Check, Loader2 } from "lucide-react";
import { useT, tr } from "@/lib/i18n";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

export const ReferralCard = ({ variant = "wallet" }) => {
  useT();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/referrals/me")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent — surface is non-critical */ });
    return () => { cancelled = true; };
  }, []);

  const onCopy = async () => {
    if (!data?.share_url) return;
    const url = data.share_url;
    const succeed = () => {
      setCopied(true);
      toast.success(tr("Link copied — share it with a colleague", "Lien copié — partagez-le avec un collègue"));
      track("referral_link_copied", { code: data.referral_code });
      setTimeout(() => setCopied(false), 2200);
    };
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return succeed();
      }
      // Fallback for older / locked-down browsers where the Clipboard API is
      // unavailable — spin up a hidden textarea and use the legacy exec API.
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) return succeed();
      throw new Error("execCommand failed");
    } catch {
      toast.error(tr("Could not copy link", "Impossible de copier le lien"));
    }
  };

  if (!data) {
    return (
      <div
        className="card p-5 flex items-center gap-3"
        data-testid="referral-card-loading"
        style={{ background: "var(--paper)" }}
      >
        <Loader2 size={16} className="animate-spin" style={{ color: "var(--muted)" }} />
        <span className="text-sm" style={{ color: "var(--muted)" }}>{tr("Loading your invite link…", "Chargement de votre lien d'invitation…")}</span>
      </div>
    );
  }

  const capped = data.remaining <= 0;
  const isWallet = variant === "wallet";

  return (
    <div
      className="card p-5"
      data-testid="referral-card"
      data-variant={variant}
      style={{
        background: isWallet ? "linear-gradient(135deg, #F6F5F2 0%, #EFEEDF 100%)" : "var(--paper)",
        border: "1px solid var(--brand-green)",
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className="flex items-center justify-center rounded"
            style={{ width: 40, height: 40, background: "var(--brand-green)", color: "#fff" }}
          >
            <Gift size={20} />
          </div>
          <div>
            <div className="overline" style={{ color: "var(--brand-green)" }}>
              {tr("Invite a colleague", "Invitez un collègue")}
            </div>
            <div
              className="font-display text-lg mt-1"
              data-testid="referral-headline"
              style={{ color: "var(--char)" }}
            >
              {isWallet
                ? tr("Give 1 credit, get 1 credit", "Offrez 1\u00a0crédit, recevez 1\u00a0crédit")
                : tr("Loved your report? Share the tool", "Votre rapport vous a plu\u00a0? Partagez l'outil")}
            </div>
            <p className="text-sm mt-1" style={{ color: "var(--muted)", maxWidth: 460 }}>
              {tr("You both earn", "Vous recevez chacun")} <b>{tr("1 free report credit", "1\u00a0crédit de rapport gratuit")}</b> {tr("when they unlock their first paid report.", "lorsque votre collègue déverrouille son premier rapport payant.")}
              {" "}{tr("Up to", "Jusqu'à")} <b>{data.cap}</b> {tr("rewards per account.", "récompenses par compte.")}
            </p>
          </div>
        </div>
        <div className="text-right" data-testid="referral-progress">
          <div className="overline" style={{ color: "var(--muted)" }}>{tr("Rewarded", "Récompensés")}</div>
          <div className="font-display text-xl mt-1" style={{ color: "var(--brand-green)" }}>
            {data.rewarded_count} / {data.cap}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {data.invited_count} {tr("invited", "invité(s)")}
          </div>
        </div>
      </div>

      <div
        className="mt-4 flex items-center gap-2 rounded"
        style={{ background: "#fff", border: "1px solid var(--rule)", padding: "8px 10px" }}
      >
        <input
          type="text"
          readOnly
          value={data.share_url}
          data-testid="referral-share-url"
          onFocus={(e) => e.target.select()}
          className="flex-1 bg-transparent outline-none text-sm"
          style={{ color: "var(--char)", fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        />
        <button
          type="button"
          onClick={onCopy}
          disabled={capped}
          data-testid="referral-copy-btn"
          className="btn-primary"
          style={{ padding: "6px 12px", fontSize: 13, opacity: capped ? 0.5 : 1 }}
          title={capped ? tr("You've hit the referral cap", "Vous avez atteint la limite de parrainages") : tr("Copy share link", "Copier le lien de partage")}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          <span className="ml-1">{copied ? tr("Copied", "Copié") : tr("Copy link", "Copier le lien")}</span>
        </button>
      </div>

      {capped ? (
        <div className="text-xs mt-3" style={{ color: "var(--muted)" }} data-testid="referral-capped-note">
          You've reached the {data.cap}-reward cap — thanks for spreading the word.
        </div>
      ) : (
        <div className="text-xs mt-3" style={{ color: "var(--muted)" }}>
          Your code: <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: "var(--char)" }} data-testid="referral-code">{data.referral_code}</span>
          &nbsp;·&nbsp; {data.remaining} reward{data.remaining === 1 ? "" : "s"} remaining
        </div>
      )}
    </div>
  );
};
